import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { usePageTitle } from '../../../hooks/shared';
import { authoritiesAPI } from '../../authorities/api/authorities';

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  broken: 'bg-red-100 text-red-700',
  not_found: 'bg-orange-100 text-orange-700',
  unknown: 'bg-neutral-100 text-neutral-500',
};

const STATUS_LABELS = {
  active: 'Active',
  broken: 'Broken',
  not_found: 'Not found',
  unknown: 'Unknown',
};

const TYPE_LABELS = {
  state: 'State',
  county: 'County',
  municipality: 'Municipality',
  school_district: 'School District',
  special_district: 'Special District',
};

const TYPE_TABS = [
  { value: '', label: 'All' },
  { value: 'state', label: 'States' },
  { value: 'county', label: 'Counties' },
  { value: 'municipality', label: 'Cities/Towns' },
  { value: 'school_district', label: 'School Districts' },
  { value: 'special_district', label: 'Special Districts' },
];

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

// ── SuggestUrlModal ─────────────────────────────────────────────────────────
function SuggestUrlModal({ authority, onClose }) {
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL (include https://)');
      return;
    }
    setSaving(true);
    try {
      await authoritiesAPI.submitUrl(authority.id, url, notes || undefined);
      setSaved(true);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {saved ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <svg
                className="w-6 h-6 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-neutral-900 mb-1">Thanks!</h3>
            <p className="text-sm text-neutral-500 mb-4">
              Your suggestion has been submitted for review.
            </p>
            <button
              onClick={onClose}
              className="text-sm text-primary-600 font-medium hover:text-primary-700"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="font-semibold text-neutral-900 mb-1">Suggest a website</h3>
            <p className="text-sm text-neutral-500 mb-4">
              For <strong>{authority.name}</strong> ({authority.state})
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Official election website URL
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setError('');
                  }}
                  placeholder="https://vote.countyname.gov/"
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Notes (optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. This is the county clerk's election page"
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={saving || !url}
                  className="flex-1 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Submitting…' : 'Submit suggestion'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Counties page ────────────────────────────────────────────────────────────
export default function Counties() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('q') ?? '';
  const stateFilter = searchParams.get('state') ?? '';
  const typeFilter = searchParams.get('type') ?? '';
  const statusFilter = searchParams.get('status') ?? '';
  const page = parseInt(searchParams.get('page') ?? '1', 10);

  const [suggestFor, setSuggestFor] = useState(null);

  function setParam(key, value) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value) next.delete(key);
        else next.set(key, value);
        next.delete('page'); // reset pagination on filter change
        return next;
      },
      { replace: true }
    );
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['authorities', search, stateFilter, typeFilter, statusFilter, page],
    queryFn: () =>
      authoritiesAPI.list({
        q: search || undefined,
        state: stateFilter || undefined,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        page,
        limit: 50,
      }),
    keepPreviousData: true,
  });

  const { data: statsData } = useQuery({
    queryKey: ['authorities-stats'],
    queryFn: authoritiesAPI.stats,
    staleTime: 5 * 60 * 1000,
  });

  usePageTitle('Election Websites Directory');

  const authorities = data?.authorities ?? [];
  const pagination = data?.pagination ?? {};
  const summary = statsData?.summary ?? {};

  const knownPct = summary.total > 0 ? Math.round((summary.active / summary.total) * 100) : 0;

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      {suggestFor && <SuggestUrlModal authority={suggestFor} onClose={() => setSuggestFor(null)} />}

      <div className="container-custom">
        {/* Header + stats */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2">
            Election Websites Directory
          </h1>
          <p className="text-neutral-600 mb-4">
            Track official election websites for every state, county, and municipality
          </p>

          {summary.total > 0 && (
            <div className="flex flex-wrap gap-4">
              <div className="bg-white rounded-xl border border-neutral-200 px-4 py-3 shadow-sm">
                <div className="text-2xl font-bold text-emerald-600">{summary.active ?? 0}</div>
                <div className="text-xs text-neutral-500">Active websites</div>
              </div>
              <div className="bg-white rounded-xl border border-neutral-200 px-4 py-3 shadow-sm">
                <div className="text-2xl font-bold text-neutral-400">{summary.unknown ?? 0}</div>
                <div className="text-xs text-neutral-500">Unknown / needs URL</div>
              </div>
              <div className="bg-white rounded-xl border border-neutral-200 px-4 py-3 shadow-sm">
                <div className="text-2xl font-bold text-neutral-900">{knownPct}%</div>
                <div className="text-xs text-neutral-500">Coverage</div>
              </div>
              {summary.broken > 0 && (
                <div className="bg-white rounded-xl border border-neutral-200 px-4 py-3 shadow-sm">
                  <div className="text-2xl font-bold text-red-500">{summary.broken}</div>
                  <div className="text-xs text-neutral-500">Broken</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Type tabs */}
        <div className="flex gap-1 flex-wrap bg-white rounded-xl border border-neutral-200 p-1 w-fit mb-4 shadow-sm">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setParam('type', tab.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                typeFilter === tab.value
                  ? 'bg-primary-600 text-white'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setParam('q', e.target.value)}
                placeholder="Search by name…"
                className="w-full pl-9 pr-4 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {typeFilter !== 'state' && (
              <select
                value={stateFilter}
                onChange={(e) => setParam('state', e.target.value)}
                className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All States</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setParam('status', e.target.value)}
              className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="unknown">Unknown</option>
              <option value="broken">Broken</option>
              <option value="not_found">Not found</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {isLoading && (
          <div className="bg-white rounded-2xl border border-neutral-200 divide-y divide-neutral-100">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="px-5 py-4 animate-pulse flex gap-4">
                <div className="h-4 bg-neutral-200 rounded w-1/3" />
                <div className="h-4 bg-neutral-200 rounded w-16" />
                <div className="h-4 bg-neutral-200 rounded w-20 ml-auto" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-16 text-neutral-500">
            Failed to load. Please try refreshing.
          </div>
        )}

        {!isLoading && !error && (
          <>
            {pagination.total != null && (
              <p className="text-sm text-neutral-500 mb-3">
                Showing {authorities.length} of {pagination.total.toLocaleString()} jurisdictions
              </p>
            )}

            <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-sm">
              {authorities.length === 0 ? (
                <div className="text-center py-16 text-neutral-500">
                  No jurisdictions match your filters.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                        Jurisdiction
                      </th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden sm:table-cell">
                        State
                      </th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden md:table-cell">
                        Type
                      </th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                        Website
                      </th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                        Status
                      </th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {authorities.map((a) => (
                      <tr key={a.id} className="hover:bg-neutral-50 transition-colors">
                        <td className="px-5 py-3">
                          <button
                            onClick={() =>
                              navigate(
                                `/counties/${a.state.toLowerCase()}/${encodeURIComponent(a.county || a.name)}`
                              )
                            }
                            className="font-medium text-neutral-900 hover:text-primary-600 text-left"
                          >
                            {a.name}
                          </button>
                        </td>
                        <td className="px-5 py-3 text-neutral-500 hidden sm:table-cell">
                          {a.state}
                        </td>
                        <td className="px-5 py-3 text-neutral-500 hidden md:table-cell">
                          {TYPE_LABELS[a.jurisdictionType] ?? a.jurisdictionType}
                        </td>
                        <td className="px-5 py-3 max-w-xs">
                          {a.websiteUrl ? (
                            <a
                              href={a.websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700 truncate block"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {new URL(a.websiteUrl).hostname}
                            </a>
                          ) : (
                            <span className="text-neutral-400 italic">Unknown</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[a.websiteStatus]}`}
                          >
                            {STATUS_LABELS[a.websiteStatus] ?? a.websiteStatus}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => setSuggestFor(a)}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                          >
                            {a.websiteUrl ? 'Suggest correction' : 'Add website'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  disabled={page <= 1}
                  onClick={() => setParam('page', String(page - 1))}
                  className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg disabled:opacity-40 hover:bg-neutral-50"
                >
                  Previous
                </button>
                <span className="text-sm text-neutral-500">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  disabled={page >= pagination.pages}
                  onClick={() => setParam('page', String(page + 1))}
                  className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg disabled:opacity-40 hover:bg-neutral-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
